import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';

/**
 * OAuth Callback Handler
 * 
 * This page receives tokens from backend OAuth flow:
 * Backend redirects to: /auth/callback?access_token=...&refresh_token=...&is_new_user=...&redirect_to=...
 * 
 * This page:
 * 1. Extracts tokens from URL
 * 2. Stores them in auth store
 * 3. Redirects to the appropriate page
 */
export function OAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuthStore();

  useEffect(() => {
    const processCallback = async () => {
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const _isNewUser = searchParams.get('is_new_user') === 'true';
      const redirectTo = searchParams.get('redirect_to') || '/dashboard';
      const error = searchParams.get('error');

      // Handle errors
      if (error) {
        console.error('OAuth error:', error);
        navigate(`/auth/login?error=${error}`, { replace: true });
        return;
      }

      // Validate tokens
      if (!accessToken || !refreshToken) {
        console.error('Missing tokens in OAuth callback');
        navigate('/auth/login?error=missing_tokens', { replace: true });
        return;
      }

      try {
        // Fetch user info with the access token
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/users/me`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user info');
        }

        const result = await response.json();
        const user = result.data;

        // Store auth state
        setAuth(user, accessToken, refreshToken);

        // Navigate to appropriate page
        // Note: Onboarding has been removed, so we always go to dashboard
        navigate(redirectTo, { replace: true });
      } catch (error) {
        console.error('Failed to process OAuth callback:', error);
        navigate('/auth/login?error=auth_failed', { replace: true });
      }
    };

    processCallback();
  }, [searchParams, navigate, setAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <div className="h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}
